import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Sparkles, Library as LibraryIcon, Users, Cpu, DollarSign, Video, ImageIcon, Loader2, Globe, RefreshCw, Brush, Film, Wand2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import VideoThumbnail from '../components/VideoThumbnail';
import { formatCredits } from '../utils/credits';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NAV_ITEMS = [
  { id: 'generate', label: 'Generate', icon: Sparkles, path: '/generate' },
  { id: 'canvas', label: 'Canvas', icon: Brush, path: '/canvas' },
  { id: 'image-to-video', label: 'Img → Vid', icon: Film, path: '/image-to-video' },
  { id: 'library', label: 'Library', icon: LibraryIcon, path: '/library' },
  { id: 'community', label: 'Community', icon: Users, path: '/community' },
];

export default function CommunityFeed() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/community/feed?limit=120`).then((r) => {
      setItems(r.data || []);
    }).finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0F] text-white flex" data-testid="community-page">
      <Sidebar location={location} user={user} />
      <main className="flex-1 overflow-y-auto">
        <header className="px-6 lg:px-10 py-8 border-b border-white/[0.05]">
          <p className="text-[10px] font-semibold tracking-[0.25em] text-[#FF6B4A] uppercase mb-2">Community</p>
          <h1 className="font-display text-3xl font-bold text-white" data-testid="community-heading">Made by DIS2ORT creators</h1>
          <p className="text-sm text-white/50 mt-1">Fresh work from the network — click any creation to riff on it.</p>
        </header>
        <div className="p-6 lg:p-10">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-6 h-6 text-[#FF6B4A] animate-spin" />
            </div>
          ) : items.length === 0 ? (
            <EmptyFeed />
          ) : (
            <div className="columns-2 sm:columns-3 lg:columns-4 xl:columns-5 gap-3 space-y-3" data-testid="community-grid">
              {items.map((job) => <CommunityCard key={job.job_id} job={job}
                onRemix={(id) => navigate(`/generate?remix=${id}`)}
                onAnimate={(path) => navigate(`/image-to-video?source=${encodeURIComponent(path)}`)} />)}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function CommunityCard({ job, onRemix, onAnimate }) {
  const [hover, setHover] = useState(false);
  const images = job.image_paths && job.image_paths.length > 0 ? job.image_paths : (job.output_path ? [job.output_path] : []);
  if (images.length === 0) return null;
  const isVideo = job.service_type === 'ai_video' || job.service_type === 'video_render';
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
      whileHover={{ y: -4, scale: 1.01 }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className="break-inside-avoid rounded-lg overflow-hidden bg-white/[0.03] border border-white/[0.05] hover:border-white/[0.2] group relative transition-colors"
      data-testid={`community-card-${job.job_id}`}
    >
      <motion.div animate={{ scale: hover ? 1.04 : 1 }} transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}>
        {isVideo ? (
          <div className="aspect-square">
            <VideoThumbnail path={images[0]} title={job.title} className="w-full h-full" />
          </div>
        ) : (
          <img src={`${API}/files/${images[0]}`} alt={job.title} className="w-full h-auto object-cover" loading="lazy" onError={(e) => { e.target.style.display = 'none'; }} />
        )}
      </motion.div>
      {/* Sheen */}
      <div className="absolute inset-0 pointer-events-none"
        style={{
          background: 'linear-gradient(115deg, transparent 38%, rgba(255,255,255,0.14) 50%, transparent 62%)',
          transform: hover ? 'translateX(110%)' : 'translateX(-110%)',
          transition: 'transform 0.85s cubic-bezier(0.16, 1, 0.3, 1)',
        }}
      />
      <div className="absolute inset-x-0 bottom-0 p-2 bg-gradient-to-t from-black/90 via-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
        <div className="flex items-center gap-1.5 mb-2">
          {job.author_picture ? <img src={job.author_picture} alt="" className="w-4 h-4 rounded-full" /> : <div className="w-4 h-4 rounded-full bg-[#FF6B4A]/40" />}
          <span className="text-[10px] text-white/80 truncate">{job.author_name || 'Anonymous'}</span>
        </div>
        <div className="text-[10px] text-white line-clamp-2 mb-2">{job.title}</div>
        <div className="flex items-center gap-1.5">
          <button onClick={() => onRemix(job.job_id)} data-testid={`community-remix-${job.job_id}`}
            className="flex-1 h-7 rounded-md bg-[#FF6B4A]/30 hover:bg-[#FF6B4A]/50 flex items-center justify-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-[#FF6B4A] transition-colors">
            <RefreshCw className="w-3 h-3" /> Remix
          </button>
          {!isVideo && onAnimate && (
            <button onClick={() => onAnimate(images[0])} title="Animate this" data-testid={`community-animate-${job.job_id}`}
              className="h-7 px-2.5 rounded-md bg-[#E040A0]/30 hover:bg-[#E040A0]/50 flex items-center justify-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#E040A0] transition-colors">
              <Wand2 className="w-3 h-3" /> Animate
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );
}

function Sidebar({ location, user }) {
  return (
    <aside className="w-[68px] lg:w-[200px] border-r border-white/[0.05] bg-[#08080C] flex flex-col">
      <Link to="/" className="h-16 flex items-center justify-center lg:justify-start lg:px-5 border-b border-white/[0.05]">
        <span className="font-body text-xl font-extrabold tracking-tight text-white">D<span className="text-[#FF6B4A]">2</span><span className="hidden lg:inline">ORT</span></span>
      </Link>
      <nav className="flex-1 py-4 px-2 lg:px-3 space-y-1">
        {NAV_ITEMS.map((it) => {
          const active = location.pathname === it.path;
          return (
            <Link key={it.id} to={it.path}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-xs font-medium transition-colors ${active ? 'bg-[#FF6B4A]/15 text-[#FF6B4A]' : 'text-white/60 hover:text-white hover:bg-white/[0.04]'}`}>
              <it.icon className="w-4 h-4 shrink-0" />
              <span className="hidden lg:inline">{it.label}</span>
            </Link>
          );
        })}
        <div className="pt-4 mt-4 border-t border-white/[0.05] space-y-1">
          <Link to="/models" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-xs font-medium text-[#FFD93D] hover:bg-[#FFD93D]/[0.06]">
            <Cpu className="w-4 h-4 shrink-0" /><span className="hidden lg:inline">150 Models</span>
          </Link>
          <Link to="/pricing" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-xs font-medium text-white/60 hover:text-white hover:bg-white/[0.04]">
            <DollarSign className="w-4 h-4 shrink-0" /><span className="hidden lg:inline">Pricing</span>
          </Link>
          <Link to="/pricing" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-xs font-medium text-white/60 hover:text-white hover:bg-white/[0.04]">
            <Sparkles className="w-4 h-4 shrink-0" /><span className="hidden lg:inline">Credits</span>
          </Link>
        </div>
      </nav>
      {user && (
        <div className="hidden lg:flex items-center gap-2 px-3 pb-4 text-white/60">
          {user.picture ? <img src={user.picture} alt="" className="w-6 h-6 rounded-full" /> : <div className="w-6 h-6 rounded-full bg-[#FF6B4A]/30" />}
          <div className="flex-1 min-w-0">
            <div className="text-[11px] text-white truncate">{user.name?.split(' ')[0]}</div>
            <div className="text-[9px] text-white/50">{formatCredits(user)} cr</div>
          </div>
        </div>
      )}
    </aside>
  );
}

function EmptyFeed() {
  return (
    <div className="text-center py-20" data-testid="empty-community">
      <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#FF6B4A]/20 to-[#E040A0]/20 flex items-center justify-center mx-auto mb-4">
        <ImageIcon className="w-7 h-7 text-[#FF6B4A]" />
      </div>
      <h2 className="font-display text-xl font-bold text-white mb-2">Community is just warming up</h2>
      <p className="text-sm text-white/50 mb-6">Be the first to publish — generate something and click the <Globe className="inline w-3.5 h-3.5 text-[#34B76F]" /> globe on it.</p>
      <Link to="/generate" className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-[#FF6B4A] text-white text-xs font-semibold hover:bg-[#FF8E72]">
        <Sparkles className="w-3.5 h-3.5" /> Generate now
      </Link>
    </div>
  );
}
